import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useRef } from 'react';
import { useDebouncedValue } from '@repo/hooks';
import { Address, getAddress } from 'viem';
import { Chain } from 'wagmi/chains';
import * as Sentry from '@sentry/nextjs';
import { useAnalyticsConsentContext } from '@/components/providers/AnalyticsConsentProvider';
import { logger } from '@/lib/observability/logger';
import { getWalletType, type WalletType } from '@/lib/observability/shared/wallet-type';
import { hashWalletAddress, safeSentryBreadcrumb } from '@/helpers/sentry';
import { identifyWallet, resetWalletIdentity, trackChainSwitched, trackWalletAccountSwitched, trackWalletConnected, trackWalletDisconnected, updateWalletTraits } from '../trackers/wallet';
interface UseWeb3AnalyticsParams {
  address: Address | undefined;
  targetChain: Chain;
  isSafe: boolean;
  isContract: boolean;
  walletProvider: string;
}
interface WalletMetadata {
  chainId: number;
  chainName: string;
  isSafe: boolean;
  isContract: boolean;
  walletProvider: string;
}

/** How long a new address must hold before a switch or disconnect counts. */
const ADDRESS_SETTLE_MS = 2_000;

/** Account switches after which a page load stops resetting the identity. */
const MAX_ACCOUNT_SWITCHES = 10;

/**
 * The address to track. A connect lands at once; a switch or disconnect waits
 * until the address holds for `ADDRESS_SETTLE_MS`.
 */
function useSettledAddress(address) {
  const $ = _c(5);
  const [settledAddress, setSettledAddressNow] = useDebouncedValue(address, ADDRESS_SETTLE_MS);
  let t0;
  let t1;
  if ($[0] !== address || $[1] !== setSettledAddressNow || $[2] !== settledAddress) {
    t0 = () => {
      if (address && !settledAddress) {
        setSettledAddressNow(address);
      }
    };
    t1 = [address, settledAddress, setSettledAddressNow];
    $[0] = address;
    $[1] = setSettledAddressNow;
    $[2] = settledAddress;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return settledAddress;
}

/** Traits sent to PostHog for the currently identified person. */
interface IdentifiedWallet {
  address: Address;
  hashedAddress: string;
  walletType: WalletType;
  walletProvider: string;
  chainId: number;
  chainName: string;
}
const reidentifyWalletAfterOptIn = ({
  identified,
  liveAddress,
  address,
  metadata,
  identifiedRef
}: {
  identified: IdentifiedWallet;
  liveAddress: Address | undefined;
  address: Address | undefined;
  metadata: WalletMetadata;
  identifiedRef: RefObject<IdentifiedWallet | undefined>;
}) => {
  // While another address settles, the live metadata describes that wallet.
  const {
    chainId,
    chainName,
    walletType,
    walletProvider
  } = liveAddress === address ? {
    ...metadata,
    walletType: getWalletType(metadata.isSafe, metadata.isContract)
  } : identified;
  identifyWallet({
    hashedAddress: identified.hashedAddress,
    chainId,
    chainName,
    walletType,
    walletProvider
  });
  identifiedRef.current = {
    ...identified,
    walletType,
    walletProvider,
    chainId,
    chainName
  };
  Sentry.setUser({
    id: identified.hashedAddress
  });
};
const trackWalletDisconnect = () => {
  safeSentryBreadcrumb({
    category: 'wallet',
    message: 'Wallet disconnected',
    level: 'info'
  });
  trackWalletDisconnected();
  resetWalletIdentity();
  Sentry.setUser(null);
};
const identifyConnectedWallet = async ({
  currentAddress,
  prevAddress,
  transition,
  transitionRef,
  metadataRef,
  identifiedRef
}: {
  currentAddress: Address;
  prevAddress: Address | undefined;
  transition: number;
  transitionRef: RefObject<number>;
  metadataRef: RefObject<WalletMetadata>;
  identifiedRef: RefObject<IdentifiedWallet | undefined>;
}) => {
  const hashedAddress = await hashWalletAddress(currentAddress);
  // A newer transition started while hashing: let it own the identity.
  if (transitionRef.current !== transition) return;
  const {
    chainId,
    chainName,
    isSafe,
    isContract,
    walletProvider
  } = metadataRef.current;
  const walletType = getWalletType(isSafe, isContract);
  const walletParams = {
    chainId,
    chainName,
    walletType,
    walletProvider
  };
  identifyWallet({
    hashedAddress,
    ...walletParams
  });
  identifiedRef.current = {
    address: currentAddress,
    hashedAddress,
    walletType,
    walletProvider,
    chainId,
    chainName
  };
  Sentry.setUser({
    id: hashedAddress
  });
  if (prevAddress) {
    trackWalletAccountSwitched(hashedAddress, walletParams);
  } else {
    safeSentryBreadcrumb({
      category: 'wallet',
      message: 'Wallet connected',
      level: 'info',
      data: {
        chainId,
        isSafe
      }
    });
    trackWalletConnected(hashedAddress, walletParams);
  }
};
const trackChainSwitch = ({
  fromChainId,
  toChainId,
  chainName,
  hasWallet
}: {
  fromChainId: number;
  toChainId: number;
  chainName: string;
  hasWallet: boolean;
}) => {
  safeSentryBreadcrumb({
    category: 'wallet',
    message: 'Chain switched',
    level: 'info',
    data: {
      fromChainId,
      toChainId,
      chainName
    }
  });
  if (hasWallet) {
    trackChainSwitched({
      fromChainId,
      toChainId,
      chainName
    });
  }
};

/**
 * Tracks wallet connection lifecycle and chain switches
 * via PostHog and Sentry.
 *
 * Identity (identify / reset / `wallet_connected`) is driven by address
 * transitions only; chain and wallet-flag changes refresh the Sentry web3
 * context and, once identified, correct the person's `wallet_type` /
 * `wallet_provider` / `chain_*` when they change.
 *
 * The address is checksummed on entry so casing differences between
 * connectors never read as a different wallet, then settled so a flapping
 * wallet does not reset the identity on every flip. Past
 * `MAX_ACCOUNT_SWITCHES` switches in one page load, further switches are
 * ignored.
 *
 * While analytics is opted out PostHog drops the `$identify` event (the
 * distinct_id is still set locally), so an opt-in with a wallet already
 * identified re-runs identify for that wallet without another lifecycle event.
 */
export function useWeb3Analytics(t0) {
  const $ = _c(31);
  const {
    address: rawAddress,
    targetChain,
    isSafe,
    isContract,
    walletProvider
  } = t0;
  let t1;
  if ($[0] !== rawAddress) {
    t1 = rawAddress ? getAddress(rawAddress) : undefined;
    $[0] = rawAddress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const liveAddress = t1;
  const address = useSettledAddress(liveAddress);
  const {
    isAnalyticsEnabled
  } = useAnalyticsConsentContext();
  let t2;
  if ($[2] !== isContract || $[3] !== isSafe || $[4] !== targetChain.id || $[5] !== targetChain.name || $[6] !== walletProvider) {
    t2 = {
      chainId: targetChain.id,
      chainName: targetChain.name,
      isSafe,
      isContract,
      walletProvider
    };
    $[2] = isContract;
    $[3] = isSafe;
    $[4] = targetChain.id;
    $[5] = targetChain.name;
    $[6] = walletProvider;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const metadataRef = useRef(t2);
  const prevAddressRef = useRef(undefined);
  const transitionRef = useRef(0);
  const accountSwitchCountRef = useRef(0);
  const identifiedRef = useRef(undefined);
  let t3;
  let t4;
  if ($[8] !== address || $[9] !== isContract || $[10] !== isSafe || $[11] !== liveAddress || $[12] !== targetChain.id || $[13] !== targetChain.name || $[14] !== walletProvider) {
    t3 = () => {
      metadataRef.current = {
        chainId: targetChain.id,
        chainName: targetChain.name,
        isSafe,
        isContract,
        walletProvider
      };
      if (address && liveAddress === address) {
        Sentry.setContext("web3", {
          chainId: targetChain.id,
          chainName: targetChain.name,
          isSafe,
          isContract
        });
        const identified = identifiedRef.current;
        if (identified?.address !== address) {
          return;
        }
        const walletType = getWalletType(isSafe, isContract);
        const chainId = targetChain.id;
        const chainName = targetChain.name;
        const traits = {
          walletType: walletType !== identified.walletType ? walletType : undefined,
          walletProvider: walletProvider !== identified.walletProvider ? walletProvider : undefined,
          chainId: chainId !== identified.chainId ? chainId : undefined,
          chainName: chainName !== identified.chainName ? chainName : undefined
        };
        if (Object.values(traits).every(_temp)) {
          return;
        }
        identifiedRef.current = {
          ...identified,
          walletType,
          walletProvider,
          chainId,
          chainName
        };
        updateWalletTraits(traits);
      }
    };
    t4 = [address, liveAddress, targetChain.id, targetChain.name, isSafe, isContract, walletProvider];
    $[8] = address;
    $[9] = isContract;
    $[10] = isSafe;
    $[11] = liveAddress;
    $[12] = targetChain.id;
    $[13] = targetChain.name;
    $[14] = walletProvider;
    $[15] = t3;
    $[16] = t4;
  } else {
    t3 = $[15];
    t4 = $[16];
  }
  useEffect(t3, t4);
  const prevAnalyticsEnabledRef = useRef(isAnalyticsEnabled);
  let t5;
  let t6;
  if ($[17] !== address || $[18] !== isAnalyticsEnabled || $[19] !== liveAddress) {
    t5 = () => {
      const wasEnabled = prevAnalyticsEnabledRef.current;
      prevAnalyticsEnabledRef.current = isAnalyticsEnabled;
      if (wasEnabled || !isAnalyticsEnabled) {
        return;
      }
      const identified_0 = identifiedRef.current;
      if (!identified_0 || identified_0.address !== address) {
        return;
      }
      ;
      try {
        reidentifyWalletAfterOptIn({
          identified: identified_0,
          liveAddress,
          address,
          metadata: metadataRef.current,
          identifiedRef
        });
      } catch (t7) {
        const error = t7;
        logger.warn("Failed to re-identify wallet after analytics opt-in", {
          extra: {
            error
          }
        });
      }
    };
    t6 = [isAnalyticsEnabled, address, liveAddress];
    $[17] = address;
    $[18] = isAnalyticsEnabled;
    $[19] = liveAddress;
    $[20] = t5;
    $[21] = t6;
  } else {
    t5 = $[20];
    t6 = $[21];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[22] !== address) {
    t7 = () => {
      const prevAddress = prevAddressRef.current;
      const currentAddress = address;
      if (prevAddress === currentAddress) {
        return;
      }
      if (prevAddress && currentAddress) {
        accountSwitchCountRef.current = accountSwitchCountRef.current + 1;
        if (accountSwitchCountRef.current > MAX_ACCOUNT_SWITCHES) {
          if (accountSwitchCountRef.current === MAX_ACCOUNT_SWITCHES + 1) {
            logger.warn("Wallet account switch limit reached, ignoring further switches", {
              extra: {
                maxSwitches: MAX_ACCOUNT_SWITCHES
              }
            });
          }
          return;
        }
      }
      prevAddressRef.current = currentAddress;
      const transition = transitionRef.current = transitionRef.current + 1;
      identifiedRef.current = undefined;
      if (!currentAddress) {
        ;
        try {
          trackWalletDisconnect();
        } catch (t9) {
          const error_0 = t9;
          logger.warn("Failed to update analytics context", {
            extra: {
              error: error_0
            }
          });
        }
        return;
      }
      if (prevAddress) {
        resetWalletIdentity();
        Sentry.setUser(null);
      }
      const identify = async () => {
        ;
        try {
          await identifyConnectedWallet({
            currentAddress,
            prevAddress,
            transition,
            transitionRef,
            metadataRef,
            identifiedRef
          });
        } catch (t10) {
          const error_1 = t10;
          logger.warn("Failed to update analytics context", {
            extra: {
              error: error_1
            }
          });
        }
      };
      identify();
    };
    t8 = [address];
    $[22] = address;
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  useEffect(t7, t8);
  const prevChainIdRef = useRef(targetChain.id);
  let t10;
  let t9;
  if ($[25] !== address || $[26] !== liveAddress || $[27] !== targetChain.id || $[28] !== targetChain.name) {
    t9 = () => {
      const prevChainId = prevChainIdRef.current;
      const currentChainId = targetChain.id;
      const hasWallet = Boolean(address && liveAddress);
      if (prevChainId !== undefined && prevChainId !== currentChainId) {
        ;
        try {
          trackChainSwitch({
            fromChainId: prevChainId,
            toChainId: currentChainId,
            chainName: targetChain.name,
            hasWallet
          });
        } catch (t11) {
          const error_2 = t11;
          logger.warn("Failed to update analytics context on chain switch", {
            extra: {
              error: error_2
            }
          });
        }
      }
      prevChainIdRef.current = currentChainId;
    };
    t10 = [targetChain.id, targetChain.name, address, liveAddress];
    $[25] = address;
    $[26] = liveAddress;
    $[27] = targetChain.id;
    $[28] = targetChain.name;
    $[29] = t10;
    $[30] = t9;
  } else {
    t10 = $[29];
    t9 = $[30];
  }
  useEffect(t9, t10);
}
function _temp(trait) {
  return trait === undefined;
}